import React, { useState, useEffect } from "react";
import axios from "axios";
import styles from "./assessmentstatus.module.css";
import { Navigate, useNavigate } from "react-router-dom";

/* ───── helpers ───── */
const fetchCompanyData = async () => {
  try {
    const res = await fetch(
      "https://casaahaanahotels.com/search_company_data",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ useremail: "" }),
      }
    );
    return await res.json();
  } catch {
    return [];
  }
};

const fetchStatusByCompany = async (admincode) => {
  const res = await axios.post(
    "https://casaahaanahotels.com/assessment_status_by_company",
    { admincode },
    { headers: { "Content-Type": "application/json" } }
  );
  return Array.isArray(res.data) ? res.data : [];
};

/* ───── component ───── */
const AssessmentStatus = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCode, setSelectedCode] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("Choose a company and click Search");
  const [statusFilter, setStatusFilter] = useState("");
  const naviage = useNavigate();
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  /* companies once  */
  useEffect(() => {
    let act = true;
    fetchCompanyData().then((d) => act && setCompanies(d));
    return () => (act = false);
  }, []);

  /* load table */
  const handleSearch = async () => {
    if (!selectedCode) {
      setMsg("Admin code is not selected.");
      setRows([]);
      return;
    }
    setMsg("");
    setLoading(true);
    try {
      const data = await fetchStatusByCompany(selectedCode);
      if (data.length === 0) setMsg("No assessments found.");
      setRows(data);
      setCurrentPage(1); // Reset to first page on new search
    } catch {
      setMsg("Failed to fetch assessment status.");
    } finally {
      setLoading(false);
    }
  };

  const filteredRows = rows.filter((r) => {
    if (!statusFilter) return true;
    const isCompleted = r.progress === "100%";
    return statusFilter === "Completed" ? isCompleted : !isCompleted;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredRows.length / itemsPerPage);
  const paginatedRows = filteredRows.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // console.log("status = ", paginatedRows);
  // Helper to generate pagination numbers with ellipsis
  const getPagination = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, "...", totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(
          1,
          "...",
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages
        );
      }
    }
    return pages;
  };

  const handlePageClick = (page) => {
    if (page === "...") return;
    setCurrentPage(page);
  };

  const handlePrev = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNext = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  /* progress cell */
  const Progress = ({ pct }) => {
    const num = parseInt(pct, 10) || 0;
    return (
      <div className={styles.progressWrapper}>
        <span className={styles.progressText}>{pct}</span>
        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{
              width: pct,
              backgroundColor: num === 100 ? "#2ecc71" : "#003366",
            }}
          />
        </div>
      </div>
    );
  };

  /* status badge */
  const Badge = ({ progress }) => (
    <span
      className={`${styles.statusBadge} ${
        progress === "100%" ? styles.statusComplete : styles.statusIncomplete
      }`}
    >
      {progress === "100%" ? "Completed" : "In Progress"}
    </span>
  );

  // console.log("rows = ", rows);
  // console.log("pagination = ", paginatedRows);
  // console.log("selectedCode = ", selectedCode);

  const handleCertificate = (selectedCode, assId) => {
    const url = `/certificate/${selectedCode}/${assId}`;
    window.open(url, "_blank");
  };
  /* ───── render ───── */
  return (
    <div className={styles.container} >
      <h2 className={styles.title}>Assessment Status</h2>

      <div className={styles.filterContainer}>
        <select
          className={styles.companyDropdown}
          value={selectedCode}
          onChange={(e) => setSelectedCode(e.target.value)}
        >
          <option value="">Select Company</option>
          {companies.map((c) => (
            <option key={c.id} value={c.admincode}>
              {c.companyname}
            </option>
          ))}
        </select>

        <select
          className={styles.companyDropdown}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="Completed">Completed</option>
          <option value="Incomplete">Incomplete</option>
        </select>

        <button onClick={handleSearch} className={styles.searchButton}>
          Search
        </button>
      </div>

      {loading ? (
        <p className={styles.loadingText}>Loading…</p>
      ) : msg ? (
        <p className={styles.errorText}>{msg}</p>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.assessmentTable}>
            <thead>
              <tr>
                <th>Driver&nbsp;Name</th>
                <th>Company&nbsp;Name</th>
                <th>Department</th>
                <th style={{ textAlign: "center" }}>Progress</th>
                <th>Status</th>
                <th>View Certificate</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRows.map((r, i) => (
                <tr key={i}>
                  <td>{r.driver_name || "Unknown"}</td>
                  <td>{r.company_name}</td>
                  <td>{r.assessment_type}</td>
                  <td style={{ textAlign: "center" }}>
                    <Progress pct={r.progress} />
                  </td>
                  <td>
                    <Badge progress={r.progress} />
                  </td>
                  <td
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {r.status === "Completed" && r.score > 2 ? (
                      <p
                        style={{
                          fontSize: "16px",
                          fontWeight: "600",
                          color: "white",
                          width: "50%",
                          textAlign: "center",
                          background: "#88E788",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          handleCertificate(selectedCode, r.assessment_id)
                        }
                      >
                        View
                      </p>
                    ) : r.score <= 2 ? (
                      <p
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "white",
                          background: "#FF7074",
                          padding: "1px 22px",
                          borderRadius: "4px",
                          cursor: "default",
                        }}
                      >
                        Fail
                      </p>
                    ) : (
                      <p
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "white",
                          background: "#FF7074",
                          padding: "3px 5px",
                          borderRadius: "4px",
                          cursor: "default",
                        }}
                      >
                        Pending
                      </p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className={styles.pagination} style={{ margin: "20px" }}>
              <button
                onClick={handlePrev}
                disabled={currentPage === 1}
                style={{
                  backgroundColor: "#003366",
                  color: "white",
                  fontWeight: "500",
                  padding: "3px 8px",
                  borderRadius: "2px",
                  marginRight: "8px",
                }}
              >
                Prev
              </button>
              {getPagination().map((page, idx) =>
                page === "..." ? (
                  <button
                    key={idx}
                    className={styles.ellipsisButton}
                    disabled
                    style={{ minWidth: 32, margin: "0 6px" }}
                  >
                    ...
                  </button>
                ) : (
                  <button
                    key={idx}
                    onClick={() => handlePageClick(page)}
                    className={
                      page === currentPage
                        ? styles.activePageButton
                        : styles.pageButton
                    }
                    style={{
                      minWidth: 32,
                      margin: "0 6px",
                      backgroundColor:
                        page === currentPage ? "#003366" : "#fff",
                      color: page === currentPage ? "white" : "#000",
                      padding: page === currentPage ? "" : "0 8px",
                    }}
                  >
                    {page}
                  </button>
                )
              )}
              <button
                onClick={handleNext}
                disabled={currentPage === totalPages}
                style={{
                  backgroundColor: "#003366",
                  color: "white",
                  fontWeight: "500",
                  padding: "3px 8px",
                  borderRadius: "2px",
                  marginLeft: "8px",
                }}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AssessmentStatus;
